<template>
  <div class="slideshow-container">
    <transition name="fade" mode="out-in">
      <div class="slide" :key="lastTimestamp">
        <LottieAnimation
          v-if="slide.animation"
          :animationName="slide.animation"
          width="70%"
          marginBottom="2rem"
        />
        <h2 class="slide-text">
          <TextHighlighting
            :key="`slide-${transitionKey}`"
            class="slide-highlight"
            :textAudioUrl="slide.url"
            :text="slide.text"
            :loadedTimestamps="timestamps"
          />
          <ProgressBar
            v-if="!slide.animation"
            :progress="highlightingProgress"
            height="8px"
            :key="`progress-${transitionKey}`"
          />
        </h2>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'

// Components
import TextHighlighting from '@/components/TextHighlighting.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import LottieAnimation from '@/components/LottieAnimation.vue'

// Stores
import { storeToRefs } from 'pinia'
import { useDataStore } from '@/stores/DataStore'

import type { QuestionTimestamp } from '@/customTypes'

const dataStore = useDataStore()
const { currentQuestionTime, screenerData } = storeToRefs(dataStore)

const props = defineProps({
  currentSlide: { type: Number, default: 0 },
  timestamps: { type: Array as () => QuestionTimestamp[], default: () => [] }
})

const slide = computed(() => screenerData.value?.metadata?.intro?.section[props.currentSlide])

const lastTimestamp = computed(() => props.timestamps[props.timestamps.length - 1]?.end)

const transitionKey: ComputedRef<number> = computed(() => props.currentSlide || 0)

const highlightingProgress: ComputedRef<number> = computed(() => {
  if (lastTimestamp.value <= currentQuestionTime.value) return 0
  const progress = (currentQuestionTime.value / lastTimestamp.value) * 100
  return progress >= 98 ? 0 : progress
})
</script>

<style lang="scss" scoped>
.slideshow-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.slide {
  position: relative;
  margin: auto;
  width: 50ch;
  max-width: calc(var(--full-vw) * 0.8);
  transform: translateY(0);
  transition:
    opacity 0.8s ease-out,
    transform 0.8s ease-out;
  opacity: 1;
  z-index: 2;
  @starting-styles {
    opacity: 0;
    transform: translateY(1rem);
  }
}

.slide-text {
  display: flex;
  align-items: center;
  flex-direction: column;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-leave-active {
  z-index: 1;
}

.slide-highlight {
  margin-bottom: calc(var(--full-vh) * 0.06);
}

@media (max-height: 500px) {
  .slide-text {
    font-size: 1.2em;
  }
}
</style>
